import React, { useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { MusicNoteIcon, FilmIcon } from "@heroicons/react/outline";

// Define supported file types and their accept patterns
const SUPPORTED_AUDIO_TYPES = {
  "audio/mpeg": [".mp3"],
  "audio/wav": [".wav"],
  "audio/x-wav": [".wav"],
  "audio/vnd.wave": [".wav"],
  "audio/x-m4a": [".m4a"],
  "audio/mp4": [".m4a"],
  "audio/aac": [".aac"],
  "audio/ogg": [".ogg"],
  "application/ogg": [".ogg"],
  "audio/vorbis": [".ogg"]
};

const SUPPORTED_VIDEO_TYPES = {
  "video/mp4": [".mp4"],
  "video/quicktime": [".mov"],
  "video/x-msvideo": [".avi"],
  "video/x-matroska": [".mkv"],
  "video/webm": [".webm"]
};

export default function FileDropzone({ file, setFile, maxSize = 300 * 1024 * 1024 }) {
  const onDrop = useCallback((acceptedFiles) => {
    const uploadedFile = acceptedFiles[0];
    if (uploadedFile) {
      if (uploadedFile.size <= maxSize) {
        setFile(uploadedFile);
      } else {
        alert(`File size exceeds the limit of ${Math.round(maxSize / (1024 * 1024))}MB.`);
      }
    } else {
      alert("Please upload a supported audio or video file.");
    }
  }, [setFile, maxSize]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { ...SUPPORTED_AUDIO_TYPES, ...SUPPORTED_VIDEO_TYPES },
    multiple: false,
    maxSize: maxSize,
  });

  const getFileIcon = (file) => {
    if (!file) return null;
    
    const fileType = file.type;
    
    // Check if it's a video file
    if (Object.keys(SUPPORTED_VIDEO_TYPES).includes(fileType)) {
      return <FilmIcon className="mx-auto h-12 w-12 text-secondary-500" />;
    }
    
    // Default to audio icon
    return <MusicNoteIcon className="mx-auto h-12 w-12 text-primary-500" />;
  };

  const getFileTypeSummary = () => {
    const audioFormats = Object.keys(SUPPORTED_AUDIO_TYPES)
      .map(type => type.split('/')[1].toUpperCase())
      .filter((value, index, self) => self.indexOf(value) === index);
    
    const videoFormats = Object.keys(SUPPORTED_VIDEO_TYPES)
      .map(type => type.split('/')[1].toUpperCase().replace('QUICKTIME', 'MOV').replace('X-MSVIDEO', 'AVI').replace('X-MATROSKA', 'MKV'))
      .filter((value, index, self) => self.indexOf(value) === index);
    
    return `Audio (${audioFormats.join(', ')}) or Video (${videoFormats.join(', ')})`;
  };

  return (
    <div
      {...getRootProps()}
      className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-neutral-300 border-dashed rounded-md hover:border-primary-400 hover:bg-primary-50 transition-colors duration-200"
    >
      <div className="space-y-1 text-center">
        {file ? (
          <div>
            {getFileIcon(file)}
            <p className="mt-2 text-sm text-neutral-700 font-medium">{file.name}</p>
            <p className="text-xs text-neutral-500">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
          </div>
        ) : (
          <>
            <svg
              className="mx-auto h-12 w-12 text-neutral-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
              aria-hidden="true"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="flex text-sm text-neutral-600">
              <label
                htmlFor="file-upload"
                className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
              >
                <span>Upload a file</span>
                <input {...getInputProps()} id="file-upload" />
              </label>
              <p className="pl-1">or drag and drop</p>
            </div>
            <div className="text-xs text-neutral-500">
              <p>{getFileTypeSummary()}</p>
              <p className="mt-1">Up to {Math.round(maxSize / (1024 * 1024))}MB</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
